import { Box, Button, useMediaQuery } from '@mui/material'
import { useCallback, useMemo, useRef } from 'react'
import { Fade, InformationModal } from 'utoniq-ui'

import { Maybe, PostDetailQuery } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  CommentList,
  CommentListProps,
  PostDetailBox,
  PostDetailBoxSkeleton,
  ReplyFormRef,
  ReplyFormValues,
} from '@/components/organisms'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'

export * from './fragments'

export type State = {
  loading: boolean
  isLoggedIn: boolean
  hasNextPage: boolean
  user: Maybe<PostDetailQuery['user']>
  post: Maybe<PostDetailQuery['post']>
  isMembershipPersonalInformationRequired: boolean
  /**
   * @description
   * コメント機能を無効化しているか否か
   * 無効化している場合はコメント関連機能を非表示にする
   */
  disableReplies?: boolean
  /**
   * @description
   * いいね機能を無効化しているか否か
   * 無効化している場合はいいね関連機能を非表示にする
   */
  disableLikes?: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onReply: (values: ReplyFormValues) => Promise<void>
  onLike: (isLike: boolean, likeCount: number) => Promise<void>
  onDeleteReply: CommentListProps['onDeleteComment']
  onReportUser: CommentListProps['onReportUser']
  onBlockUser: CommentListProps['onBlockUser']
  onLikeOrUnlikeReply: CommentListProps['onLikeOrUnlikeComment']
  onReportArtist?: (
    postId: string,
    artistId: string,
    accountId: string
  ) => Promise<void>
  onClickShare: () => Promise<void>
}

export const PostDetailTemplate: Template<State, Action> = ({
  state,
  onReply,
  onLike,
  onDeleteReply,
  onReportUser,
  onBlockUser,
  onReportArtist,
  onLikeOrUnlikeReply,
  onFetchMore,
  onClickShare,
}) => {
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ postId: string }>()
  const replyFormRef = useRef<ReplyFormRef>(null)
  const { showMessage } = useMessage()
  const { t } = useTranslation('postDetail')
  const isPcView = useMediaQuery(theme => theme.breakpoints.up('md'))
  const displayResponsive = individualDisplayResponsive()

  const shouldShowPcOptimizedLayout = useMemo(() => {
    return isPcView && displayResponsive
  }, [isPcView, displayResponsive])

  const handleReportArtist = useCallback(async () => {
    if (!onReportArtist) return

    await onReportArtist(
      state.post?.id ?? '',
      state.post?.artist?.id ?? '',
      state.post?.artist?.formattedAccountId ?? ''
    )
  }, [
    onReportArtist,
    state.post?.artist?.formattedAccountId,
    state.post?.artist?.id,
    state.post?.id,
  ])

  const handleBlockArtist = useCallback(async () => {
    await onBlockUser(state.post?.artistId ?? '')
    showMessage(t('blockArtistSuccess'), 'success')
    router.push(
      generatePath(routerPages.ArtistTop, {
        artistId: state.post?.artistId,
      })
    )
  }, [
    generatePath,
    onBlockUser,
    router,
    routerPages.ArtistTop,
    showMessage,
    state.post?.artistId,
    t,
  ])

  if (!state.loading && !state.post) {
    return <NotFound useNextRouter={false} />
  }
  // ユーザーが加入しているメンバーシップよりも上位プランであるかどうか
  const isGraterPlan = !state?.post?.isAccessible && state?.post?.planId

  // アクセス可能かつコメント機能が有効であるかどうか
  const shouldShowReplies = state.post?.isAccessible && !state.disableReplies

  return (
    <ArtistThemeProvider artistId={state.post?.artistId}>
      <BaseLayout
        navigation={
          /** レスポンシブ適用対象でPC表示の場合は画面の右側に設置するため、コメント入力欄はナビゲーションからは非表示にする */
          shouldShowReplies && !shouldShowPcOptimizedLayout
            ? {
                type: 'replyForm',
                props: {
                  ref: replyFormRef,
                  onReply,
                  userId: state.user?.id ?? '',
                },
                responsive: displayResponsive,
              }
            : undefined
        }
        header={{
          back: true,
          defaultBackPage:
            state.post?.artistId &&
            generatePath(routerPages.ArtistTop, {
              artistId: state.post?.artistId,
            }),
          ...(displayResponsive
            ? {
                responsive: true,
                maxWidth: 1120,
              }
            : {}),
        }}
        maxWidth={displayResponsive ? 1120 : undefined}
      >
        <Head
          title={[routerNames.PostDetail, state.post?.artist.displayName]}
        />
        <Box
          sx={
            shouldShowPcOptimizedLayout
              ? { display: 'flex', justifyContent: 'center', gap: 1 }
              : {}
          }
        >
          {state.loading && (
            <PostDetailBoxSkeleton
              sx={
                shouldShowPcOptimizedLayout
                  ? { maxWidth: '736px', flex: 2 }
                  : {}
              }
            />
          )}
          {!state.loading && state?.post && (
            <Fade
              sx={
                shouldShowPcOptimizedLayout
                  ? { maxWidth: '736px', flex: 2 }
                  : {}
              }
            >
              <PostDetailBox
                userId={state.user?.id}
                post={state.post}
                isMembershipPersonalInformationRequired={
                  state.isMembershipPersonalInformationRequired
                }
                onLike={() => {
                  if (!state.post) return
                  onLike(!!state?.post.isLike, state?.post.likeCount)
                }}
                onReply={() => replyFormRef.current?.focus()}
                onBlockArtist={handleBlockArtist}
                onReportArtist={handleReportArtist}
                enableEmbedHtml={true}
                onClickShare={onClickShare}
                {...(displayResponsive
                  ? {
                      responsive: true,
                      detail: true,
                    }
                  : {})}
              />
            </Fade>
          )}
          {!state?.loading && !state?.post?.isAccessible && (
            <InformationModal
              onClose={() =>
                router.goBack(
                  generatePath(routerPages.ArtistTop, {
                    artistId: state?.post?.artist.id,
                  })
                )
              }
              open={true}
              title={
                isGraterPlan
                  ? t('viewMemberInfoTitleWithDiff')
                  : t('viewMemberInfoTitle')
              }
              //planTitleが存在しない場合に空文字を表示しているが、運用上planTitleはisGraterPlanがtureの場合必ず存在する
              content={
                isGraterPlan
                  ? t('viewMemberInfoDescWithDiff', {
                      planTitle: state?.post?.plan?.planTitle ?? '',
                    })
                  : t('viewMemberInfoDesc', {
                      displayName: state?.post?.artist.displayName,
                    })
              }
              cancelText={t('close')}
              buttons={
                <Button
                  size="large"
                  color="primary"
                  sx={{ minWidth: 212 }}
                  href={
                    state?.post?.artist?.id
                      ? generatePath(routerPages.ArtistMembership, {
                          artistId: state?.post.artist.id,
                        })
                      : undefined
                  }
                >
                  {t('seeDetail')}
                </Button>
              }
            />
          )}
          {/**
           * ローディング中の場合はコメント部分をスケルトン表示すべく、コメント機能が有効であることが確定していなくても表示する。
           * state.loadingがtrueであればCommentListのLoadingComponentが呼ばれるため、コメント機能は使えない。
           */}
          {(state.loading || shouldShowReplies) &&
            (shouldShowPcOptimizedLayout ? (
              <Box sx={{ flex: 1 }}>
                <CommentList
                  loading={state.loading}
                  hasNextPage={state.hasNextPage}
                  commentCount={state.post?.replyCount}
                  comments={state.post?.replies ?? null}
                  user={state.user ?? null}
                  onFetchMore={onFetchMore}
                  onDeleteComment={onDeleteReply}
                  onReportUser={onReportUser}
                  onBlockUser={onBlockUser}
                  showReaction
                  onLikeOrUnlikeComment={onLikeOrUnlikeReply}
                  hiddenReply
                  onClickReply={() => {}}
                  onReplyToReply={() => {}}
                  disableLikes={state.disableLikes}
                  // コメント入力欄とヘッダを表示するのはコメント機能が有効であることが確定してから。ローディング中は表示しない
                  replyFormProps={
                    shouldShowReplies
                      ? {
                          ref: replyFormRef,
                          onReply,
                          userId: state.user?.id ?? '',
                        }
                      : undefined
                  }
                  hiddenHeader={!shouldShowReplies}
                />
              </Box>
            ) : (
              <CommentList
                loading={state.loading}
                hasNextPage={state.hasNextPage}
                commentCount={state.post?.replyCount}
                comments={state.post?.replies ?? null}
                user={state.user ?? null}
                onFetchMore={onFetchMore}
                onDeleteComment={onDeleteReply}
                onReportUser={onReportUser}
                onBlockUser={onBlockUser}
                showReaction
                onLikeOrUnlikeComment={onLikeOrUnlikeReply}
                hiddenReply
                onClickReply={() => {}}
                onReplyToReply={() => {}}
                disableLikes={state.disableLikes}
                // ヘッダを表示するのはコメント機能が有効であることが確定してから。ローディング中は表示しない
                hiddenHeader={!shouldShowReplies}
              />
            ))}
        </Box>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
